import type { RequestCategory } from "@nlc/shared-types";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import { useGetDesignTypes } from "../hooks/useGetDesignTypes";
import { designTypeMatchesQuery, normalizeSearchQuery } from "../utils";

function categoryHasMatch(
  categoryId: string,
  query: string,
  data: ReturnType<typeof useGetDesignTypes>["data"],
) {
  const subcategoryIds = new Set(
    data.subcategories
      .filter((sub) => sub.category === categoryId)
      .map((sub) => sub.id),
  );
  const normalizedQuery = normalizeSearchQuery(query);

  return data.designTypes.some(
    (type) =>
      subcategoryIds.has(type.subcategoryId) &&
      designTypeMatchesQuery(type, normalizedQuery),
  );
}

export function usePickTypeState() {
  const { data } = useGetDesignTypes();
  const [searchQuery, setSearchQuery] = useState("");

  const firstUnblockedCategory = data.categories.find(
    (cat) => !cat.isBlocked,
  )?.id;

  const {
    category,
    ["project-id"]: projectIdFromSearch,
    subcategory = 0,
  } = useSearch({
    from: "/_auth/_app-shell/requests/pick-type",
  });
  const projectId = useGetCurrentProjectId();
  const navigate = useNavigate({ from: "/requests/pick-type" });

  useEffect(() => {
    // Reset search params when projectId changes or category is not set
    if (!projectIdFromSearch || !category) {
      navigate({
        search: {
          category: firstUnblockedCategory,
          ["project-id"]: projectId,
          subcategory: 0,
        },
      });
    }
  }, [
    projectIdFromSearch,
    projectId,
    navigate,
    category,
    firstUnblockedCategory,
  ]);

  const handleCategoryChange = (newCategory: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        category: newCategory as RequestCategory,
        subcategory: 0,
      }),
    });
  };

  const handleSubcategoryChange = (newSubcategory: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        subcategory: Number(newSubcategory),
      }),
    });
  };

  const handleManualCategoryChange = (newCategory: string) => {
    setSearchQuery("");
    handleCategoryChange(newCategory);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value) {
      handleSubcategoryChange("0");
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const matchingCategories = normalizeSearchQuery(searchQuery)
    ? data.categories.filter(
        (cat) => !cat.isBlocked && categoryHasMatch(cat.id, searchQuery, data),
      )
    : [];

  const singleMatchCategoryId =
    matchingCategories.length === 1 ? matchingCategories[0]?.id : undefined;

  useEffect(() => {
    if (!category || !singleMatchCategoryId) return;
    if (singleMatchCategoryId === category) return;

    handleCategoryChange(singleMatchCategoryId);
  }, [category, singleMatchCategoryId]);

  return {
    category: category || firstUnblockedCategory,
    handleClearSearch,
    handleManualCategoryChange,
    handleSearchChange,
    handleSubcategoryChange,
    matchingCategories,
    searchQuery,
    subcategory: subcategory.toString(),
  };
}
